import { newId } from './ids';
import { V2_SCHEMA_VERSION } from './inspection';
import type { NonClientTestContextMarker } from './inspectionContext';
import type { ResponsibleTrade } from './responsibleTrade';
import type { GateAction, GateProhibitedHit, GateReason, GateSuppression, SuggestionScope } from './scopeGate';

// AI observations (Final Owner Decision 2026-09-14, ratification CA-4 / CA-5 / D-CA-4).
//
// Two record kinds, both local (IndexedDB) and both OUTSIDE the inspector's official state:
//   V2AiRun          one area analysis request with its outcome, usage and the Scope Gate trail;
//   V2AiObservation  one thing the AI saw. An observation is EVIDENCE the inspector reads on the
//                    checklist row it belongs to (③ of the item sheet). It is never accepted, adopted
//                    or rejected: the inspector's own result on the row (④) is the only decision, and
//                    the observation neither sets it nor is changed by it. An observation the AI could
//                    not match to a row is UNCLASSIFIED until the inspector files it into a row,
//                    records it as an other finding, records the limitation it points at, or ignores it.
// AI never writes an ItemStatus and never creates a finding (OWNER-CONTRACT §4 / §7; CA-8).
export type AiObservationKind = 'existingItem' | 'suggestedItem' | 'otherIssue' | 'needsClarification' | 'noAction';

export const AI_OBSERVATION_KIND_ORDER: readonly AiObservationKind[] = Object.freeze(['existingItem', 'suggestedItem', 'otherIssue', 'needsClarification', 'noAction']);

export function isAiObservationKind(value: unknown): value is AiObservationKind {
  return typeof value === 'string' && (AI_OBSERVATION_KIND_ORDER as readonly string[]).includes(value);
}

export type AiConfidence = 'low' | 'medium' | 'high';

export function isAiConfidence(value: unknown): value is AiConfidence {
  return value === 'low' || value === 'medium' || value === 'high';
}

// 2026-10-18: what the observation concludes, as a closed value (the wire's AiV2Verdict). The screen
// shows the confidence next to every observation and, for the two conclusions a photograph cannot
// settle, the line 「AI 没发现 ≠ 无异常，请自行确认」 / 「需现场测试，照片无法判断」.
export type AiVerdict = 'defect' | 'noVisibleDefect' | 'fieldTestRequired' | 'unclear';

export function isAiVerdict(value: unknown): value is AiVerdict {
  return value === 'defect' || value === 'noVisibleDefect' || value === 'fieldTestRequired' || value === 'unclear';
}

export type AiRunStatus = 'running' | 'done' | 'failed';

export interface V2AiRun {
  schemaVersion: number;
  id: string;
  inspectionId: string;
  areaId: string;
  photoIds: string[];
  scanNote: string;
  status: AiRunStatus;
  errorCode: string | null;
  // Provider / usage metadata as reported by the backend (null until done).
  model: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  durationMs: number | null;
  requestId: string | null;
  summary: string;
  // NON_CLIENT_TEST_CONTEXT when this run was a Demo / internal test rather than a New York client
  // engagement (frozen plan §15.2 / §17); null for a client run. Derived at creation from the same
  // classification that allows the call — never supplied by a caller.
  contextMarker: NonClientTestContextMarker | null;
  // NY F1 Round 4 (plan §9; AI policy §3 outputs): the Scope Gate's decision on EVERY result of this
  // run — the ones that became observations and the ones that did not. The audit trail the authority
  // asks for ("which rule and which source decided this?").
  gateLog: AiGateLogEntry[];
  startedAt: number;
  finishedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

// One line of the run's gate trail. The original AI wording is kept verbatim so a later reader can
// see what was rejected, not only that something was.
export interface AiGateLogEntry {
  resultId: string;
  // The observation this result became, or null when nothing was surfaced.
  observationId: string | null;
  action: GateAction;
  reason: GateReason;
  subjectId: string | null;
  disposition: string | null;
  legalStatus: string | null;
  suppressed: GateSuppression | null;
  ruleIds: string[];
  authorityVersion: string;
  prohibitedHits: GateProhibitedHit[];
  original: { type: AiObservationKind; itemId: string | null; title: string; headline: string; observation: string; recommendation: string };
}

// The structured content of one result, shared by the wire protocol and the stored observation.
export interface AiObservationContent {
  type: AiObservationKind;
  // Existing checklist item the observation belongs to (existingItem only).
  itemId: string | null;
  // Short subject (App Language).
  title: string;
  // One-line gist (App Language).
  headline: string;
  // What the AI saw (Report Language).
  observation: string;
  // Possible issue, plain words (App Language).
  concern: string;
  // What it might be / what the photos show (App Language).
  guidance: string;
  checkSteps: string[];
  // What the AI would recommend (Report Language); '' when nothing.
  recommendation: string;
  responsibleTrade: ResponsibleTrade | null;
  // The two report-language sentences rendered in the App Language for the screen — null when the
  // locales match.
  observationUi?: string | null;
  recommendationUi?: string | null;
  confidence: AiConfidence;
  // 2026-10-18: the closed conclusion; absent on records written before the field (the schema derives it).
  verdict?: AiVerdict | null;
  // Clarification question for the inspector (needsClarification).
  question: string | null;
  scopeNote: string | null;
  // Related photos (evidence ids); one result may reference several, one photo several results.
  photoIds: string[];
}

// What the inspector did with an UNCLASSIFIED observation (CA-5). null = still open. An observation
// that belongs to a row (itemId set) needs no resolution: the row's result is the decision.
export type AiObservationResolution =
  | { kind: 'filedToItem'; itemId: string; at: number }
  | { kind: 'otherFinding'; findingId: string; at: number }
  | { kind: 'limitation'; itemId: string; at: number }
  | { kind: 'noted'; at: number };

export const AI_OBSERVATION_RESOLUTION_KINDS: readonly AiObservationResolution['kind'][] = Object.freeze(['filedToItem', 'otherFinding', 'limitation', 'noted']);

export function isAiObservationResolutionKind(value: unknown): value is AiObservationResolution['kind'] {
  return typeof value === 'string' && (AI_OBSERVATION_RESOLUTION_KINDS as readonly string[]).includes(value);
}

export interface V2AiObservation extends AiObservationContent {
  schemaVersion: number;
  id: string;
  inspectionId: string;
  areaId: string;
  runId: string;
  // NY F1 Round 4: what the Scope Gate decided about this result — the disposition it carries, the
  // rules that decided it, the actions the inspector may take on it, and (OD-13) whether it is an
  // ordinary observation, a scope reminder or a limitation prompt.
  scope: SuggestionScope | null;
  resolution: AiObservationResolution | null;
  sortOrder: number;
  createdAt: number;
  updatedAt: number;
}

export interface NewAiRunInput {
  inspectionId: string;
  areaId: string;
  photoIds: string[];
  scanNote: string;
  contextMarker?: NonClientTestContextMarker | null;
}

export function createAiRun(input: NewAiRunInput, now: number): V2AiRun {
  return {
    schemaVersion: V2_SCHEMA_VERSION,
    id: newId('airun'),
    inspectionId: input.inspectionId,
    areaId: input.areaId,
    photoIds: [...input.photoIds],
    scanNote: input.scanNote,
    status: 'running',
    errorCode: null,
    model: null,
    inputTokens: null,
    outputTokens: null,
    durationMs: null,
    requestId: null,
    summary: '',
    contextMarker: input.contextMarker ?? null,
    gateLog: [],
    startedAt: now,
    finishedAt: null,
    createdAt: now,
    updatedAt: now
  };
}

export interface AiRunUsage {
  model: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  durationMs: number | null;
  requestId: string | null;
}

export function withRunDone(run: V2AiRun, summary: string, usage: AiRunUsage, now: number): V2AiRun {
  return { ...run, status: 'done', errorCode: null, summary, ...usage, finishedAt: now, updatedAt: now };
}

export function withRunFailed(run: V2AiRun, errorCode: string, now: number): V2AiRun {
  return { ...run, status: 'failed', errorCode, finishedAt: now, updatedAt: now };
}

// The Scope Gate trail of a finished run (NY F1 Round 4). Written once, with the run.
export function withRunGateLog(run: V2AiRun, gateLog: readonly AiGateLogEntry[], now: number): V2AiRun {
  return { ...run, gateLog: gateLog.map((entry) => ({ ...entry })), updatedAt: Math.max(run.updatedAt, now) };
}

export function createAiObservation(
  run: Pick<V2AiRun, 'id' | 'inspectionId' | 'areaId'>,
  content: AiObservationContent,
  sortOrder: number,
  now: number,
  scope: SuggestionScope | null = null
): V2AiObservation {
  return {
    schemaVersion: V2_SCHEMA_VERSION,
    id: newId('aiobs'),
    inspectionId: run.inspectionId,
    areaId: run.areaId,
    runId: run.id,
    ...content,
    checkSteps: [...content.checkSteps],
    photoIds: [...new Set(content.photoIds)],
    scope,
    resolution: null,
    sortOrder,
    createdAt: now,
    updatedAt: now
  };
}

// An observation that belongs to a checklist row: shown in that row's ③, never in the unclassified list.
export function observationBelongsToRow(observation: Pick<V2AiObservation, 'itemId' | 'type'>): boolean {
  return observation.itemId !== null && observation.type !== 'noAction';
}

// Still waiting for the inspector's choice in the unclassified list (CA-5).
export function observationIsUnclassified(observation: Pick<V2AiObservation, 'itemId' | 'type' | 'resolution'>): boolean {
  return observation.itemId === null && observation.type !== 'noAction' && observation.resolution === null;
}

// 归入现有检查项: the observation becomes a row observation of `itemId`. Its photos are linked to the row
// by the store (subject to the inspector's photo overrides); nothing else changes.
export function withObservationFiledToItem(observation: V2AiObservation, itemId: string, now: number): V2AiObservation {
  return { ...observation, itemId, resolution: { kind: 'filedToItem', itemId, at: now }, updatedAt: now };
}

export function withObservationOtherFinding(observation: V2AiObservation, findingId: string, now: number): V2AiObservation {
  return { ...observation, resolution: { kind: 'otherFinding', findingId, at: now }, updatedAt: now };
}

export function withObservationLimitation(observation: V2AiObservation, itemId: string, now: number): V2AiObservation {
  return { ...observation, resolution: { kind: 'limitation', itemId, at: now }, updatedAt: now };
}

export function withObservationNoted(observation: V2AiObservation, now: number): V2AiObservation {
  return { ...observation, resolution: { kind: 'noted', at: now }, updatedAt: now };
}
